import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Card,
  Alert,
  CircularProgress,
  ListSubheader,
  Button,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Pagination,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import SendIcon from "@mui/icons-material/Send";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import HistoryIcon from "@mui/icons-material/History";
import DeleteIcon from "@mui/icons-material/Delete";
import ApiEndpoint from "../../config/ApiEndpoint";
import ApiServices from "../../config/ApiServices";
import { toast } from "react-toastify";
import { PageHeader, ActionButton } from "../shared";

const MAX_MESSAGE_LENGTH = 500;
const USERS_FETCH_LIMIT = 5000;

const Alerts = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const profile = props?.props?.profile || props?.props?.profile?.data || {};
  const token = profile?.token || props?.props?.profile?.token;
  const userType =
    profile?.user_type || props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;
  const currentUserId = profile?.id || props?.props?.profile?.id || props?.props?.profile?.data?.id;
  const senderName =
    profile?.organisation_name?.trim() ||
    profile?.display_name?.trim() ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() ||
    null;

  const [alertTab, setAlertTab] = useState(0); // 0 = to users, 1 = to sub-orgs, 2 = history

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [message, setMessage] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [sending, setSending] = useState(false);

  const [subOrgs, setSubOrgs] = useState([]);
  const [loadingSubOrgs, setLoadingSubOrgs] = useState(true);
  const [subOrgIds, setSubOrgIds] = useState([]);
  const [sendingToOrgs, setSendingToOrgs] = useState(false);

  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [alertPage, setAlertPage] = useState(1);

  const headers = useCallback(
    () => ({ "Content-Type": "application/json", "x-access-token": token }),
    [token]
  );

  const loadUsers = async () => {
    if (!token) return;
    setLoadingUsers(true);
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_USER_LIST,
        JSON.stringify({ type: "user", pagination: true, page: 1, limit: USERS_FETCH_LIMIT, srch: "" }),
        headers()
      );
      setLoadingUsers(false);
      if (data?.status && Array.isArray(data.data)) {
        setUsers(
          data.data.filter(
            (item) => !["schools", "individual", "master_org", "sub_org", "master", "admin"].includes(item.role)
          )
        );
      } else {
        setUsers([]);
      }
    } catch {
      setLoadingUsers(false);
      setUsers([]);
      toast.error("Could not load users. Please refresh and try again.");
    }
  };

  const loadSubOrgs = async () => {
    if (!token) return;
    setLoadingSubOrgs(true);
    try {
      const data = await ApiServices.GetApiCall(ApiEndpoint.ADMIN_ORGS_ALL, headers());
      setLoadingSubOrgs(false);
      if (data?.status) {
        setSubOrgs(data.data?.sub_orgs || []);
      } else {
        setSubOrgs([]);
      }
    } catch {
      setLoadingSubOrgs(false);
      setSubOrgs([]);
      toast.error("Could not load clubs. Please refresh and try again.");
    }
  };

  const loadAlerts = useCallback(async () => {
    if (!token) return;
    setLoadingAlerts(true);
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_ALERTS_LIST,
        JSON.stringify({}),
        headers()
      );
      setLoadingAlerts(false);
      const rows = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.alerts)
        ? data.data.alerts
        : Array.isArray(data?.alerts)
        ? data.alerts
        : [];
      setAlerts(rows);
      setAlertPage(1);
    } catch {
      setLoadingAlerts(false);
      setAlerts([]);
      toast.error("Could not load alert history. Please refresh and try again.");
    }
  }, [token, headers]);

  useEffect(() => {
    if (userType === "admin" && token) {
      loadUsers();
      loadSubOrgs();
    }
  }, [userType, token]);

  useEffect(() => {
    if (userType === "admin" && token) {
      loadAlerts();
    }
  }, [userType, token, loadAlerts]);

  const handleSend = async () => {
    const trimmed = (message || "").trim();
    if (!trimmed) { toast.error("Please enter a message."); return; }
    if (trimmed.length > MAX_MESSAGE_LENGTH) { toast.error(`Message must be at most ${MAX_MESSAGE_LENGTH} characters.`); return; }
    if (!selectedRoles.length) { toast.error("Please select at least one role."); return; }
    if (!token) return;

    const roleUserIds = selectableUsers
      .filter((u) => selectedRoles.includes(u.role))
      .map((u) => Number(u.id));

    if (!roleUserIds.length) { toast.error("No users found for the selected roles."); return; }

    setSending(true);
    const body = { message: trimmed, user_ids: roleUserIds, target_members: selectedRoles };
    if (senderName) body.sender_name = senderName;

    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_ALERTS_SEND,
        JSON.stringify(body),
        headers()
      );
      setSending(false);
      if (data?.status) {
        toast.success(
          data.message || `Alert sent to ${data?.data?.recipients_count ?? roleUserIds.length} recipient(s).`
        );
        setMessage("");
        setSelectedRoles([]);
        loadAlerts();
      } else {
        toast.error(data?.message || "Failed to send alert.");
      }
    } catch {
      setSending(false);
      toast.error("Failed to send alert.");
    }
  };

  const handleSendToSubOrgs = async () => {
    const trimmed = (message || "").trim();
    if (!trimmed) { toast.error("Please enter a message."); return; }
    if (trimmed.length > MAX_MESSAGE_LENGTH) { toast.error(`Message must be at most ${MAX_MESSAGE_LENGTH} characters.`); return; }
    if (!token) return;

    setSendingToOrgs(true);
    const body = { message: trimmed };
    if (senderName) body.sender_name = senderName;
    if (subOrgIds.length > 0) body.org_ids = subOrgIds.map((id) => Number(id));

    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_ALERTS_SEND_TO_ORGS,
        JSON.stringify(body),
        headers()
      );
      setSendingToOrgs(false);
      if (data?.status) {
        toast.success(data.message || "Alert sent successfully.");
        setMessage("");
        setSubOrgIds([]);
        loadAlerts();
      } else {
        toast.error(data?.message || "Failed to send alert.");
      }
    } catch {
      setSendingToOrgs(false);
      toast.error("Failed to send alert.");
    }
  };

  const handleDeleteAlert = async (alertId) => {
    setDeletingId(alertId);
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_ALERTS_DELETE,
        JSON.stringify({ alert_id: alertId }),
        headers()
      );
      setDeletingId(null);
      if (data?.status) {
        toast.success("Alert deleted.");
        setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      } else {
        toast.error(data?.message || "Failed to delete alert.");
      }
    } catch {
      setDeletingId(null);
      toast.error("Failed to delete alert.");
    }
    setConfirmDeleteId(null);
  };

  const selectableUsers = users.filter((u) => u.id !== currentUserId);
  const availableRoles = [...new Set(selectableUsers.map((u) => u.role).filter(Boolean))].sort();
  const formatRole = (role) => {
    if (!role || typeof role !== "string") return String(role ?? "");
    return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, " ");
  };

  const handleSelectAllRoles = (e) => { e.stopPropagation(); setSelectedRoles([...availableRoles]); };
  const handleClearRoles = (e) => { e.stopPropagation(); setSelectedRoles([]); };
  const allSubOrgIds = subOrgs.map((o) => o.id);
  const handleSelectAllSubOrgs = (e) => { e.stopPropagation(); setSubOrgIds([...allSubOrgIds]); };
  const handleClearSubOrgs = (e) => { e.stopPropagation(); setSubOrgIds([]); };

  const cardSx = {
    p: 0,
    overflow: "hidden",
    backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
    border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0",
  };
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "#242424" : "#f7fafc",
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" },
    },
  };
  const tabsSx = {
    borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
    minHeight: 48,
    "& .MuiTab-root": { textTransform: "none", fontWeight: 500, minHeight: 48, px: { xs: 1.5, sm: 2 }, fontSize: { xs: "0.8125rem", sm: "0.875rem" } },
    "& .MuiTabs-flexContainer": { gap: 0 },
  };

  if (userType !== "admin") {
    return (
      <Box>
        <PageHeader title="Alerts" subtitle="Access denied." />
        <Card>
          <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            This page is for platform admin accounts only.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Alerts"
        subtitle="Send short alert messages to selected users or clubs."
        breadcrumbs={[{ label: "Alerts" }]}
      />

      <Card sx={cardSx}>
        <Tabs
          value={alertTab}
          onChange={(_, v) => setAlertTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={tabsSx}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label="To users" />
          <Tab icon={<GroupIcon />} iconPosition="start" label="To Clubs" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="History" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {alertTab === 0 && (
            <>
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  backgroundColor: isDark ? "rgba(99, 102, 241, 0.1)" : "rgba(25, 118, 210, 0.08)",
                  color: isDark ? "#cbd5e1" : "#1e3a5f",
                }}
              >
                Select one or more roles — all users belonging to those roles will receive the alert (max {MAX_MESSAGE_LENGTH} characters).
              </Alert>

              <TextField
                label="Message"
                placeholder="e.g. Reminder: reports due Friday."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                rows={4}
                fullWidth
                inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
                helperText={`${(message || "").length} / ${MAX_MESSAGE_LENGTH}`}
                error={(message || "").length > MAX_MESSAGE_LENGTH}
                sx={{ mb: 3, ...inputSx }}
              />

              <FormControl fullWidth required sx={{ mb: 3, ...inputSx }}>
                <InputLabel id="admin-alerts-roles-label">Recipient roles (select at least one)</InputLabel>
                <Select
                  labelId="admin-alerts-roles-label"
                  multiple
                  value={selectedRoles}
                  onChange={(e) => setSelectedRoles(e.target.value)}
                  input={<OutlinedInput label="Recipient roles (select at least one)" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <Typography sx={{ color: isDark ? "#718096" : "#718096", fontSize: "0.875rem" }}>Select roles…</Typography>;
                    }
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((role) => (
                          <Chip key={role} label={formatRole(role)} size="small" sx={{ backgroundColor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />
                        ))}
                      </Box>
                    );
                  }}
                  disabled={loadingUsers}
                  MenuProps={{ autoFocus: false, slotProps: { root: { onMouseDown: (e) => e.stopPropagation() } } }}
                >
                  <ListSubheader sx={{ lineHeight: 1, py: 1, px: 1.5, display: "flex", gap: 1, flexWrap: "wrap", backgroundColor: "transparent" }}>
                    <Button size="small" onClick={handleSelectAllRoles} onMouseDown={(e) => e.stopPropagation()} disabled={loadingUsers || availableRoles.length === 0} sx={{ textTransform: "none", fontSize: "0.8125rem", color: isDark ? "#818cf8" : "#1976d2", minWidth: "auto", px: 1 }}>Select all</Button>
                    <Button size="small" onClick={handleClearRoles} onMouseDown={(e) => e.stopPropagation()} disabled={loadingUsers} sx={{ textTransform: "none", fontSize: "0.8125rem", color: isDark ? "#94a3b8" : "#64748b", minWidth: "auto", px: 1 }}>Clear</Button>
                  </ListSubheader>
                  {availableRoles.map((role) => {
                    const count = selectableUsers.filter((u) => u.role === role).length;
                    return (
                      <MenuItem key={role} value={role}>
                        {formatRole(role)}
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: isDark ? "#94a3b8" : "#64748b" }}>
                          ({count} user{count !== 1 ? "s" : ""})
                        </Typography>
                      </MenuItem>
                    );
                  })}
                </Select>
                {loadingUsers && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Loading roles…</Typography>
                  </Box>
                )}
                {!loadingUsers && selectedRoles.length > 0 && (
                  <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", mt: 0.5, display: "block" }}>
                    {selectableUsers.filter((u) => selectedRoles.includes(u.role)).length} user(s) will receive this alert.
                  </Typography>
                )}
              </FormControl>

              <ActionButton
                variant="primary"
                startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                onClick={handleSend}
                disabled={sending || !(message || "").trim() || selectedRoles.length === 0}
              >
                {sending ? "Sending..." : "Send Alert"}
              </ActionButton>
            </>
          )}

          {alertTab === 1 && (
            <>
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  backgroundColor: isDark ? "rgba(99, 102, 241, 0.1)" : "rgba(25, 118, 210, 0.08)",
                  color: isDark ? "#cbd5e1" : "#1e3a5f",
                }}
              >
                Alerts are short messages (max {MAX_MESSAGE_LENGTH} characters) sent to club admins. Leave "Clubs" empty to send to all clubs.
              </Alert>

              <TextField
                label="Message"
                placeholder="e.g. Reminder: reports due Friday."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                rows={4}
                fullWidth
                inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
                helperText={`${(message || "").length} / ${MAX_MESSAGE_LENGTH}`}
                error={(message || "").length > MAX_MESSAGE_LENGTH}
                sx={{ mb: 3, ...inputSx }}
              />

              <FormControl fullWidth sx={{ mb: 3, ...inputSx }}>
                <InputLabel id="admin-alerts-suborgs-label">Clubs (optional)</InputLabel>
                <Select
                  labelId="admin-alerts-suborgs-label"
                  multiple
                  value={subOrgIds}
                  onChange={(e) => setSubOrgIds(e.target.value)}
                  input={<OutlinedInput label="Clubs (optional)" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <Typography sx={{ color: isDark ? "#718096" : "#718096", fontSize: "0.875rem" }}>All clubs</Typography>;
                    }
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((id) => {
                          const org = subOrgs.find((o) => o.id === id);
                          return <Chip key={id} label={org?.name || org?.organisation_name || id} size="small" sx={{ backgroundColor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />;
                        })}
                      </Box>
                    );
                  }}
                  disabled={loadingSubOrgs}
                  MenuProps={{ autoFocus: false, slotProps: { root: { onMouseDown: (e) => e.stopPropagation() } } }}
                >
                  <ListSubheader sx={{ lineHeight: 1, py: 1, px: 1.5, display: "flex", gap: 1, flexWrap: "wrap", backgroundColor: "transparent" }}>
                    <Button size="small" onClick={handleSelectAllSubOrgs} onMouseDown={(e) => e.stopPropagation()} disabled={loadingSubOrgs || subOrgs.length === 0} sx={{ textTransform: "none", fontSize: "0.8125rem", color: isDark ? "#818cf8" : "#1976d2", minWidth: "auto", px: 1 }}>Select all</Button>
                    <Button size="small" onClick={handleClearSubOrgs} onMouseDown={(e) => e.stopPropagation()} disabled={loadingSubOrgs} sx={{ textTransform: "none", fontSize: "0.8125rem", color: isDark ? "#94a3b8" : "#64748b", minWidth: "auto", px: 1 }}>Clear</Button>
                  </ListSubheader>
                  {subOrgs.map((org) => (
                    <MenuItem key={org.id} value={org.id}>{org.name || org.organisation_name || `Org #${org.id}`}</MenuItem>
                  ))}
                </Select>
                {loadingSubOrgs && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Loading clubs…</Typography>
                  </Box>
                )}
                <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", mt: 0.5, display: "block" }}>
                  Leave empty to send to all clubs.
                </Typography>
              </FormControl>

              <ActionButton
                variant="primary"
                startIcon={sendingToOrgs ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                onClick={handleSendToSubOrgs}
                disabled={sendingToOrgs || !(message || "").trim()}
              >
                {sendingToOrgs ? "Sending..." : "Send Alert"}
              </ActionButton>
            </>
          )}

          {alertTab === 2 && (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                  Sent Alerts
                </Typography>
                <Button
                  size="small"
                  onClick={loadAlerts}
                  disabled={loadingAlerts}
                  sx={{ textTransform: "none", color: isDark ? "#818cf8" : "#1976d2" }}
                >
                  {loadingAlerts ? <CircularProgress size={16} /> : "Refresh"}
                </Button>
              </Box>

              {loadingAlerts ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : alerts.length === 0 ? (
                <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", textAlign: "center", py: 6 }}>
                  No alerts sent yet.
                </Typography>
              ) : isMobile ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {alerts.slice((alertPage - 1) * 15, alertPage * 15).map((alert) => (
                    <Box
                      key={alert.id}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid #e2e8f0",
                        backgroundColor: isDark ? "#242424" : "#f8fafc",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ color: isDark ? "#cbd5e1" : "#1e293b", whiteSpace: "pre-wrap", wordBreak: "break-word", flex: 1 }}>
                          {alert.message}
                        </Typography>
                        <Tooltip title="Delete alert">
                          <IconButton
                            size="small"
                            onClick={() => setConfirmDeleteId(alert.id)}
                            disabled={deletingId === alert.id}
                            sx={{ color: isDark ? "#f87171" : "#dc2626", flexShrink: 0, mt: -0.25 }}
                          >
                            {deletingId === alert.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {alert.sender_name && (
                        <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8", display: "block", mb: 0.75 }}>
                          From: {alert.sender_name}
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {alert.sent_to?.type === "clubs" ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                              {(alert.sent_to.details || []).map((t) => (
                                <Chip key={t.id ?? t.name} label={t.name || "—"} size="small" sx={{ height: 20, fontSize: "0.6875rem", backgroundColor: isDark ? "rgba(99,102,241,0.15)" : "rgba(25,118,210,0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />
                              ))}
                            </Box>
                          ) : alert.sent_to?.type === "role" ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                              {(Array.isArray(alert.sent_to.role)
                                ? alert.sent_to.role
                                : (alert.sent_to.role || "").split(",").map((r) => r.trim())
                              ).filter(Boolean).map((r) => (
                                <Chip key={r} label={formatRole(r)} size="small" sx={{ height: 20, fontSize: "0.6875rem", backgroundColor: isDark ? "rgba(99,102,241,0.15)" : "rgba(25,118,210,0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />
                              ))}
                            </Box>
                          ) : alert.sent_to?.type === "users" ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                              {(alert.sent_to.details || []).map((r) => (
                                <Chip key={r.id_user ?? r.id} label={[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"} size="small" sx={{ height: 20, fontSize: "0.6875rem", backgroundColor: isDark ? "rgba(99,102,241,0.15)" : "rgba(25,118,210,0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8" }}>—</Typography>
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8", whiteSpace: "nowrap", flexShrink: 0 }}>
                          {alert.sent_at || alert.created_at
                            ? new Date(alert.sent_at || alert.created_at).toLocaleString()
                            : "—"}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600, fontSize: "0.8125rem", width: "45%" }}>Message</TableCell>
                        <TableCell sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600, fontSize: "0.8125rem", width: "35%" }}>Sent to</TableCell>
                        <TableCell sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600, fontSize: "0.75rem", width: "15%" }}>Date</TableCell>
                        <TableCell align="right" sx={{ width: "5%" }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alerts.slice((alertPage - 1) * 15, alertPage * 15).map((alert) => (
                        <TableRow
                          key={alert.id}
                          sx={{
                            "&:last-child td": { border: 0 },
                            "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" },
                          }}
                        >
                          <TableCell sx={{ color: isDark ? "#cbd5e1" : "#1e293b", maxWidth: 340 }}>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {alert.message}
                            </Typography>
                            {alert.sender_name && (
                              <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8" }}>
                                From: {alert.sender_name}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8125rem", maxWidth: 220 }}>
                            {alert.sent_to?.type === "clubs" ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {(alert.sent_to.details || []).map((t) => (
                                  <Chip key={t.id ?? t.name} label={t.name || "—"} size="small" sx={{ height: 20, fontSize: "0.6875rem", backgroundColor: isDark ? "rgba(99,102,241,0.15)" : "rgba(25,118,210,0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />
                                ))}
                              </Box>
                            ) : alert.sent_to?.type === "role" ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {(Array.isArray(alert.sent_to.role)
                                  ? alert.sent_to.role
                                  : (alert.sent_to.role || "").split(",").map((r) => r.trim())
                                ).filter(Boolean).map((r) => (
                                  <Chip key={r} label={formatRole(r)} size="small" sx={{ height: 20, fontSize: "0.6875rem", backgroundColor: isDark ? "rgba(99,102,241,0.15)" : "rgba(25,118,210,0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />
                                ))}
                              </Box>
                            ) : alert.sent_to?.type === "users" ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {(alert.sent_to.details || []).map((r) => (
                                  <Chip key={r.id_user ?? r.id} label={[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"} size="small" sx={{ height: 20, fontSize: "0.6875rem", backgroundColor: isDark ? "rgba(99,102,241,0.15)" : "rgba(25,118,210,0.08)", color: isDark ? "#818cf8" : "#1976d2" }} />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: isDark ? "#64748b" : "#94a3b8", fontSize: "0.8125rem" }}>—</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: isDark ? "#94a3b8" : "#64748b", whiteSpace: "nowrap", fontSize: "0.75rem" }}>
                            {alert.sent_at || alert.created_at
                              ? new Date(alert.sent_at || alert.created_at).toLocaleString()
                              : "—"}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Delete alert">
                              <IconButton
                                size="small"
                                onClick={() => setConfirmDeleteId(alert.id)}
                                disabled={deletingId === alert.id}
                                sx={{ color: isDark ? "#f87171" : "#dc2626" }}
                              >
                                {deletingId === alert.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {!loadingAlerts && alerts.length > 15 && (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                  <Pagination
                    count={Math.ceil(alerts.length / 15)}
                    page={alertPage}
                    onChange={(_, v) => setAlertPage(v)}
                    size="small"
                    sx={{ "& .MuiPaginationItem-root": { color: isDark ? "#94a3b8" : "#64748b" } }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Card>

      <Dialog
        open={confirmDeleteId !== null}
        onClose={() => { if (!deletingId) setConfirmDeleteId(null); }}
        disableEscapeKeyDown={!!deletingId}
        PaperProps={{
          sx: { backgroundColor: isDark ? "#1e1e1e" : "#fff", backgroundImage: "none", minWidth: 320 },
        }}
      >
        <DialogTitle sx={{ color: isDark ? "#e2e8f0" : "#1e293b" }}>Delete alert?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
            This will permanently delete the alert and all associated data. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)} sx={{ textTransform: "none", color: isDark ? "#94a3b8" : "#64748b" }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteAlert(confirmDeleteId)}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            disabled={deletingId === confirmDeleteId}
          >
            {deletingId === confirmDeleteId ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Alerts;
